import { TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React, { ReactElement, useCallback } from 'react';

import { CompositeSessionInfoSelectors, SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { isTrialSite, useSelector } from '~src/settings/general-settings/utils';
import { useGetSiteTrial } from '~src/settings/hooks/useGetSiteTrial';
import { TrialType } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { GenerativeAIRegistration } from '../tableau-gai/GenerativeAIRegistration';
import { GenerativeAIRegistrationHookType, useGenerativeAIRegistrationHook } from '../tableau-gai/GenerativeAIRegistrationApi';
import { GenerativeAISectionProps } from '../tableau-gai/GenerativeAISection';
import { TryTableauAISection } from '../try-tableau-ai/TryTableauAISection';
import { AgentPermissionsTable } from './AgentPermissionsTable';
import { styles } from './AgentSectionStyles';
import { getTestId } from './AgentSectionsUtils';
import { SettingsForAgents, SettingsForAgents_enabled, SettingsForAgents_groupId } from './AgentSectionTypes';

const SF_LEGAL_URL = 'https://www.salesforce.com/company/legal/trust-and-compliance-documentation/';

function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = !!useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  // Cloud only. On Server the permissions table renders inside GenerativeAIOnPremSection instead,
  // so this section's Cloud-oriented chrome (the feature explainer and legal terms) stays hidden.
  return isSiteOrServerAdmin && isOnlineEnvironment && FeatureFlags.agentPermissionsTableEnabled;
}

export type AgentPermissionsSectionProps_enabled = SiteSettingsForSiteAdminProps<SettingsForAgents_enabled>;
export type AgentPermissionsSectionProps_groupId = SiteSettingsForSiteAdminProps<SettingsForAgents_groupId>;
export type AgentPermissionsSectionProps = SiteSettingsForSiteAdminProps<SettingsForAgents>;

export const AgentPermissionsSection = (props: AgentPermissionsSectionProps): ReactElement => {
  const formik = useFormikContext<AgentPermissionsSectionProps & GenerativeAISectionProps>();
  const { registrationState, registerEnterprise, unregisterEnterprise, alertMessage, setAlertMessage, resetAlertMessage } =
    useGenerativeAIRegistrationHook(formik);
  const showSettings = useShowSettingsSection();
  const trialSite = isTrialSite();
  const isOnline = !!useSelector(SessionServerSelectors.isOnlineEnvironment);

  const siteSettingsV2 = formik.values.settings.siteSettingsV2;
  const isTableauPlus = !!siteSettingsV2?.pulsePremiumGAIEnabled || !!siteSettingsV2?.einsteinCopilotForTableauEnabled;
  const isTryItMode = FeatureFlags.tabAITryItEnabled && !trialSite && !isTableauPlus;
  const [tableauAITrial, activateTableauAITrial] = useGetSiteTrial(TrialType.TABLEAU_AI);

  const handleActivateTableauAITrial = useCallback(async () => {
    const activated = await activateTableauAITrial();
    if (!activated) {
      return;
    }

    const newSiteSettings = await Sites.instance.getCurrentSiteSettingsForSiteAdmin();
    formik.resetForm({
      values: {
        ...formik.values,
        settings: {
          ...formik.values.settings,
          ...newSiteSettings.siteAdminSettings,
        },
      },
    });
  }, [activateTableauAITrial, formik]);

  if (!showSettings) {
    return null;
  }

  return (
    <SettingsSection
      title={Messages.settings_agents_permissions_section_title()}
      testId={'agent-permissions-settings-section'}
      description={
        <div>
          <p>{Messages.settings_agents_permissions_description_p1()}</p>
          <p className={styles.descriptionParagraph}>
            {Messages.settings_agents_permissions_description_p2()}
            {' '}
            <TextLinkWidget href={HelpService.getHelpUrlByCategory('gpt-core')} target="_blank">
              {Messages.learnMore()}
            </TextLinkWidget>
          </p>
        </div>
      }
    >
      <div>
        {isOnline && !trialSite && (
          <>
            {isTryItMode ? (
              <TryTableauAISection onActivate={handleActivateTableauAITrial} tableauAITrial={tableauAITrial} />
            ) : (
              <ConnectToSalesforceAndModelSettings
                gaiHook={{
                  registrationState,
                  registerEnterprise,
                  unregisterEnterprise,
                  alertMessage,
                  setAlertMessage,
                  resetAlertMessage,
                }}
              />
            )}
          </>
        )}
        <p className={styles.explainerHeader}>
          <b>{Messages.settings_agents_explainer_header()}</b>
        </p>
        <p>
          {Messages.settings_agents_explainer_text()}
          <TextLinkWidget
            href={HelpService.getHelpUrlByCategory('gpt-salesforce-login')}
            target="_blank"
            testId={getTestId('ai_help_link_learn_more')}
          >
            {Messages.learnMore()}
          </TextLinkWidget>
        </p>
        <AgentPermissionsTable
          allowTestRegistration={!FeatureFlags.tabGAITokenServiceRegistration}
          formik={formik}
          isTrialSite={isOnline && trialSite}
          isTryItMode={isOnline && isTryItMode}
          registered={isOnline ? registrationState.registered : true}
          tableauAITrial={tableauAITrial}
          isOnline={isOnline}
        />
        {isOnline && trialSite && <TrialSection />}
        <LegalSection />
      </div>
    </SettingsSection>
  );
};

function ConnectToSalesforceAndModelSettings(props: {
  gaiHook: GenerativeAIRegistrationHookType;
}): ReactElement {
  return (
    <>
      <p>
        <b>{Messages.settings_agents_connect_to_salesforce_header()}</b>
      </p>
      <GenerativeAIRegistration
        name={'generative-ai-setting-registration'}
        testId={'generative-ai-setting-registration'}
        gaiHook={props.gaiHook}
        connectionDetails={Messages.settings_agents_connect_to_salesforce_details()}
      />
    </>
  );
}

function TrialSection(): ReactElement {
  return (
    <div id="trialDisclaimer">
      <p>
        <span className={styles.trialDisclaimer}>{Messages.settings_agents_trial_message()}</span>
      </p>
    </div>
  );
}

function LegalSection(): ReactElement {
  return (
    <>
      <p id="generative-ai-setting-lg-terms-1">{Messages.tableauGAISiteSettingsLTerm1()}</p>
      <div id="generative-ai-setting-lg-terms-2">
        {Messages.tableauGAISiteSettingsLTerm2()}
        <ul>
          <li id="generative-ai-setting-lg-terms-3">{Messages.tableauGAISiteSettingsLTerm3()}</li>
          <li id="generative-ai-setting-lg-terms-4">{Messages.tableauGAISiteSettingsLTerm4()}</li>
        </ul>
      </div>
      <div id="generative-ai-setting-lg-terms-5">
        {Messages.tableauGAISiteSettingsLTerm5()}
        <TextLinkWidget href={SF_LEGAL_URL} target="_blank" testId={getTestId('LegalSection_link')}>
          {Messages.tableauGAISiteSettingsLTerm6()}
        </TextLinkWidget>
      </div>
    </>
  );
}

/* for registering the component in settings registry */
export const AgentPermissionsSectionSpecs = componentRegistryEntry({
  name: 'AgentPermissionsSection',
  component: AgentPermissionsSection,
});
